<template>
  <section
    v-if="currentSlide"
    class="relative w-full overflow-x-hidden hero-section-enter lg:h-[637px] lg:overflow-hidden"
    @touchstart.passive="onTouchStart"
    @touchend.passive="onTouchEnd"
  >
    <!-- MOBILE LAYOUT (< lg) -->
    <div class="lg:hidden flex flex-col">
      <!-- FIRST DIV: Top Section (Figma 3056-7632) - Hero frame is 271px tall -->
      <div class="relative h-[271px] hero-background-enter">
        <!-- Background Image (mobile-specific, no overlay) -->
        <TransitionGroup name="slide-fade">
          <div
            v-for="(slide, index) in slides"
            v-show="currentIndex === index"
            :key="'mobile-bg-' + slide.id"
            class="absolute inset-0"
          >
            <img
              :src="slide.backgroundImageMobile || slide.backgroundImage"
              :alt="slide.title"
              :class="[
                'w-full h-full',
                slide.objectFitMobile
                  ? `object-${slide.objectFitMobile}`
                  : slide.objectFit
                    ? `object-${slide.objectFit}`
                    : 'object-cover',
              ]"
              :style="
                slide.objectPosition
                  ? { objectPosition: slide.objectPosition }
                  : {}
              "
            />
            <div
              v-if="slide.overlayGradient"
              class="absolute inset-0 pointer-events-none"
              style="
                background: linear-gradient(
                  180deg,
                  rgba(31, 41, 55, 0.6) 0%,
                  rgba(31, 41, 55, 0.3) 100%
                );
              "
            />
          </div>
        </TransitionGroup>

        <!-- Calligraphy: 244x244px, from Figma x=70,y=9 in 393px frame → centered horizontally -->
        <div
          class="absolute inset-0 flex items-center justify-center hero-calligraphy-enter"
        >
          <Transition name="calligraphy-fade" mode="out-in">
            <div
              v-if="currentSlide.calligraphyImage"
              :key="'mobile-calli-' + currentSlide.id"
              class="w-[80%] h-[80%] pointer-events-none overflow-hidden"
            >
              <img
                :src="currentSlide.calligraphyImage"
                alt="Arabic Calligraphy"
                class="w-full h-full object-contain"
              />
            </div>
          </Transition>
        </div>

        <!-- Social Links: Figma x=278,y=207 in 393px frame → right=19px, top=207px -->
        <div class="absolute right-[29px] top-[207px] flex hero-social-enter">
          <UiSocialLinks />
        </div>
      </div>

      <!-- SECOND DIV: Bottom Content Card (Figma 3056-7643) -->
      <div
        class="bg-[#1f2937] px-5 py-6 w-full max-h-[calc(100vh-271px)] overflow-y-auto hero-content-enter"
      >
        <div class="relative min-h-[320px]">
          <Transition name="card-fade">
            <div :key="currentIndex" class="flex flex-col gap-6 w-full mb-6">
              <!-- Date Badge -->
              <div
                class="bg-[#fff9ec] border border-solid border-[#d0a444] rounded-[38px] px-3.5 py-2 self-start"
              >
                <p
                  class="text-[#d0a444] text-[13px] font-semibold font-outfit leading-[15.96px]"
                >
                  {{ currentSlide.dateBadge }}
                </p>
              </div>

              <!-- Title + Description -->
              <div class="flex flex-col gap-3 w-full">
                <h2
                  class="text-white text-[26px] font-semibold font-outfit leading-[40.8px] tracking-[-0.68px]"
                >
                  {{ currentSlide.title }}
                </h2>
                <p
                  class="text-white/80 text-base font-medium font-outfit leading-[1.6]"
                >
                  {{ currentSlide.description }}
                </p>
              </div>

              <!-- CTA Button -->
              <a
                :href="currentSlide.ctaLink"
                class="bg-[#d0a444] rounded-[30px] px-6 py-3.5 text-white text-base font-semibold font-outfit leading-5 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-all hover:bg-[#d0a444]/90 self-start"
              >
                {{ currentSlide.ctaText }}
              </a>

              <!-- Price -->
              <div
                v-if="currentSlide.price"
                class="flex flex-col items-end gap-0.5"
              >
                <div class="flex items-start gap-1">
                  <p
                    class="text-white font-semibold text-sm font-outfit leading-5"
                  >
                    À partir de
                  </p>
                  <div class="flex items-start gap-1">
                    <span
                      class="text-white font-semibold text-[13px] font-inter leading-none mt-0.5"
                      >€</span
                    >
                    <span
                      class="text-white font-black text-[28px] font-inter leading-none tracking-[0px]"
                      >{{ currentSlide.price }},00</span
                    >
                  </div>
                </div>
                <p
                  class="text-white/70 text-[13px] font-semibold font-inter leading-snug text-right"
                >
                  / par pèlerin
                </p>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Bottom Controls Section -->
        <div
          class="flex items-center justify-between w-full hero-controls-enter gap-3"
        >
          <!-- Slider Dots -->
          <div class="flex items-center gap-[6px] min-w-0 flex-1">
            <button
              v-for="(s, idx) in slides"
              :key="'mobile-dot-' + s.id"
              @click="goToSlide(idx)"
              :class="[
                'transition-all duration-300 flex-1 max-w-[51px]',
                currentIndex === idx
                  ? 'h-[3px] bg-[#d0a444]'
                  : 'h-[2px] bg-[rgba(217,217,217,0.3)]',
              ]"
              :aria-label="`Aller à la slide ${idx + 1}`"
            />
          </div>

          <!-- Navigation Arrows -->
          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              @click="prevSlide"
              class="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-[28px] bg-white/10 border-2 border-solid border-white/30 text-white/70 transition-all hover:bg-[#d0a444] hover:text-white"
              aria-label="Slide précédente"
            >
              <IconRight :size="14" class="rotate-180" />
            </button>
            <button
              @click="nextSlide"
              class="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-[28px] bg-white/10 border-2 border-solid border-white/30 text-white/70 transition-all hover:bg-[#d0a444]/90 hover:text-white"
              aria-label="Slide suivante"
            >
              <IconRight :size="14" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- DESKTOP LAYOUT (lg+) -->
    <div class="hidden lg:block absolute inset-0 hero-desktop-enter">
      <!-- Background Layer with slide transition -->
      <TransitionGroup name="slide-fade">
        <div
          v-for="(slide, index) in slides"
          v-show="currentIndex === index"
          :key="slide.id"
          class="absolute inset-0"
        >
          <!-- Background Image -->
          <img
            :src="slide.backgroundImage"
            :alt="slide.title"
            :class="[
              'absolute inset-0 w-full h-full',
              slide.objectFitDesktop
                ? `object-${slide.objectFitDesktop}`
                : slide.objectFit
                  ? `object-${slide.objectFit}`
                  : 'object-cover',
            ]"
            :style="
              slide.objectPosition
                ? { objectPosition: slide.objectPosition }
                : {}
            "
          />
          <!-- Gradient overlay for photo-based slides -->
          <div
            v-if="slide.overlayGradient"
            class="absolute inset-0 pointer-events-none"
            style="
              background: linear-gradient(
                90deg,
                #1f2937 0%,
                #1f2937 40.06%,
                rgba(31, 41, 55, 0) 100%
              );
            "
          />
        </div>
      </TransitionGroup>

      <!-- Arabic Calligraphy - Right Side (Gold) - positioned relative to container -->
      <div
        class="absolute inset-0 flex justify-center hero-calligraphy-desktop-enter pointer-events-none"
      >
        <Transition name="calligraphy-fade" mode="out-in">
          <div
            v-if="currentSlide.calligraphyImage"
            :key="currentSlide.id"
            class="absolute inset-0 flex justify-center"
          >
            <div class="relative w-full max-w-container">
              <div
                class="calligraphy-wrap absolute top-1/2 -translate-y-1/2 pointer-events-none overflow-hidden"
              >
                <!-- Arabic Calligraphy Image (PNG/SVG) -->
                <img
                  :src="currentSlide.calligraphyImage"
                  alt="Arabic Calligraphy"
                  class="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <!-- Content Panel with Blur - Aligned to container -->
      <div
        class="absolute inset-0 flex justify-center hero-content-desktop-enter"
      >
        <div class="relative w-full max-w-container h-full">
          <!-- Blur panel - starts at container edge, 690px wide -->
          <div
            class="absolute top-0 left-4 sm:left-6 lg:left-8 2xl:left-10 h-full w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] lg:w-[690px]"
          >
            <!-- Backdrop blur panel -->
            <div
              class="absolute inset-0 backdrop-blur-[25px] bg-[#1F2937]/80"
            ></div>

            <!-- Content with inner padding -->
            <div class="relative h-full w-full">
              <!-- Content Container with slide animation -->
              <div class="relative h-full overflow-hidden">
                <TransitionGroup name="slide-content">
                  <div
                    v-for="(slide, index) in slides"
                    v-show="currentIndex === index"
                    :key="slide.id"
                    class="absolute inset-0 flex flex-col gap-10 items-start px-6 lg:px-[45px] pt-[144px]"
                  >
                    <!-- Slide Content -->
                    <div class="flex flex-col gap-5 w-full">
                      <div class="flex flex-col gap-6 items-start">
                        <!-- Date Badge -->
                        <div
                          class="bg-primary-light border border-primary rounded-[38px] px-5 py-1.5"
                        >
                          <span
                            class="text-[#8a6b22] text-md font-semibold font-outfit"
                          >
                            {{ slide.dateBadge }}
                          </span>
                        </div>

                        <!-- Title -->
                        <h2
                          class="text-white text-3xl lg:text-[40px] font-semibold font-outfit leading-tight lg:leading-[40.8px] tracking-[-0.68px]"
                        >
                          {{ slide.title }}
                        </h2>
                      </div>

                      <!-- Description -->
                      <p
                        class="text-white/80 text-base font-medium font-outfit leading-[1.6] max-w-[485px]"
                      >
                        {{ slide.description }}
                      </p>
                    </div>

                    <!-- CTA Button + Price -->
                    <div class="flex flex-col gap-5 items-start">
                      <a
                        :href="slide.ctaLink"
                        class="inline-flex items-center justify-center bg-primary rounded-[30px] px-6 py-3.5 text-white text-lg font-semibold font-outfit leading-5 shadow-sm transition-all hover:bg-primary/90 hover:shadow-lg"
                      >
                        {{ slide.ctaText }}
                      </a>
                      <div
                        v-if="slide.price"
                        class="flex flex-col items-end gap-0.5"
                      >
                        <div class="flex items-start gap-1">
                          <p
                            class="text-white/70 font-medium text-sm font-outfit leading-5"
                          >
                            À partir de
                          </p>
                          <div class="flex items-start gap-1">
                            <span
                              class="text-white font-semibold text-[13px] font-inter leading-none mt-0.5"
                              >€</span
                            >
                            <span
                              class="text-white font-black text-[28px] font-inter leading-none tracking-[0px]"
                              >{{ slide.price }},00</span
                            >
                          </div>
                        </div>
                        <p
                          class="text-white/70 text-[13px] font-semibold font-inter leading-snug text-right"
                        >
                          / par pèlerin
                        </p>
                      </div>
                    </div>
                  </div>
                </TransitionGroup>
              </div>

              <!-- Navigation Arrows - Inside panel, bottom left -->
              <div
                class="absolute bottom-[40px] left-[40px] hidden lg:flex items-center gap-2.5"
              >
                <!-- Previous Button - Dark with border -->
                <button
                  @click="prevSlide"
                  class="flex h-[43px] w-[43px] items-center justify-center rounded-full border-2 border-gray-500 bg-gray-700 hover:bg-primary hover:border-[#E8D2A3] text-white/70 transition-all hover:text-white"
                  aria-label="Slide précédente"
                >
                  <IconRight :size="15" class="rotate-180" />
                </button>
                <!-- Next Button - Primary/Gold -->
                <button
                  @click="nextSlide"
                  class="flex h-[43px] w-[43px] items-center justify-center rounded-full border-2 border-gray-500 bg-gray-700 hover:bg-primary hover:border-[#E8D2A3] text-white/70 hover:text-white transition-all"
                  aria-label="Slide suivante"
                >
                  <IconRight :size="15" />
                </button>
              </div>

              <!-- Slide Indicators - Inside panel, bottom right (horizontal bars) -->
              <div
                class="absolute bottom-[43px] right-[45px] hidden lg:flex items-center gap-2.5"
              >
                <button
                  v-for="(slide, index) in slides"
                  :key="'indicator-' + slide.id"
                  @click="goToSlide(index)"
                  :class="[
                    'w-[51px] rounded-sm transition-all duration-300',
                    currentIndex === index
                      ? 'h-[3px] bg-primary'
                      : 'h-[2px] bg-white/30 hover:bg-white/50',
                  ]"
                  :aria-label="`Aller à la slide ${index + 1}`"
                />
              </div>

              <!-- Mobile Indicators + Progress (inside panel) -->
              <div
                class="absolute left-6 right-6 bottom-6 flex flex-col gap-3 lg:hidden"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <button
                      v-for="(slide, index) in slides"
                      :key="'mobile-dot-' + slide.id"
                      @click="goToSlide(index)"
                      class="h-2 w-2 rounded-full transition-colors"
                      :class="
                        currentIndex === index ? 'bg-primary' : 'bg-white/40'
                      "
                      :aria-label="`Aller à la slide ${index + 1}`"
                    />
                  </div>
                  <span class="text-xs font-medium text-white/70">
                    Faites glisser
                  </span>
                </div>
                <div class="h-1 rounded-full bg-white/20 overflow-hidden">
                  <div
                    class="h-full bg-primary transition-[width] duration-150"
                    :style="{ width: `${progressPercent}%` }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Social Links - Outside panel, bottom right of hero section -->
      <div
        class="absolute right-[43px] bottom-[40px] flex hero-social-desktop-enter"
      >
        <UiSocialLinks />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import IconArrowRight from "../icons/IconArrowRight.vue";
import IconRight from "../icons/IconRight.vue";
import { gregorianToHijriYear } from "~/utils/date-utils";

interface Slide {
  id: number;
  dateBadge: string;
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  backgroundImage: string;
  backgroundImageMobile?: string;
  calligraphyImage?: string;
  price?: string;
  overlayGradient?: boolean;
  objectPosition?: string;
  /** Controls background fit on mobile (small screens) */
  objectFitMobile?: "cover" | "contain";
  /** Controls background fit on desktop (lg+) */
  objectFitDesktop?: "cover" | "contain";
  /** Fallback when specific breakpoint settings are not provided */
  objectFit?: "cover" | "contain";
}

interface Props {
  slides?: Slide[];
}

const props = withDefaults(defineProps<Props>(), {
  slides: () => [
    {
      id: 1,
      dateBadge: "Départ 02/04/2026 · 10 Jours",
      title: "Omra Al Baraka",
      description:
        "Partez accomplir votre Omra dans les meilleures conditions avec la formule Omra Al Baraka, un programme de 10 jours conçu pour allier spiritualité, confort et sérénité.",
      ctaText: "Commencer la réservation",
      ctaLink: "/omra/omra-al-baraka?date=2026-04-02",
      backgroundImage: "/images/slider-baraka-bkg.webp",
      backgroundImageMobile: "/images/slider-hero-al-baraka-mobile.webp",
      price: "1690",
      overlayGradient: true,
    },
    {
      id: 2,
      dateBadge: "Départ 02/04/2026 · 10 Jours",
      title: "Omra Al Khayr",
      description:
        "Partez accomplir votre Omra dans les meilleures conditions avec la formule Omra Al Khayr, un programme de 10 jours conçu pour allier spiritualité, confort et sérénité.",
      ctaText: "Commencer la réservation",
      ctaLink: "/omra/omra-al-khayr?date=2026-04-02",
      backgroundImage: "/images/slider-khayr-bkg.webp",
      backgroundImageMobile: "/images/slider-hero-al-khayr-mobile.webp",
      price: "1990",
      overlayGradient: true,
    },
    {
      id: 3,
      dateBadge: `Ramadhan ${new Date().getFullYear()} / ${gregorianToHijriYear(new Date())} H`,
      title: `Omra Ramadhan ${new Date().getFullYear()}/${gregorianToHijriYear(new Date())}h`,
      description: `L'Omra Ramadhan ${new Date().getFullYear()} propose un séjour spirituel de 30 jours, incluant Madinah et Makkah avec hébergements soigneusement sélectionnés, confortables, organisés.`,
      ctaText: "Voir nos formules",
      ctaLink: "/omra-ramadhan",
      backgroundImage: "/images/slider-ramadhan-bkg.webp",
      backgroundImageMobile: "/images/slider-ramadhan-bkg-mobile.webp",
      calligraphyImage: "/images/ramadhan-logo.svg",
    },
    {
      id: 4,
      dateBadge: "Disponible toute l'année",
      title: `Découvrez nos Omras ${new Date().getFullYear()}`,
      description:
        "Tayba Travel vous accompagne dans un séjour spirituel d’exception, avec des départs hebdomadaires vers les trois sites saints et des hébergements choisis avec soin pour garantir confort, sérénité et organisation optimale.",
      ctaText: "Voir nos formules",
      ctaLink: "/omra",
      backgroundImage: "/images/slider-omra-bkg.webp",
      backgroundImageMobile: "/images/slider-omra-bkg-mobile.webp",
      calligraphyImage: "/images/omra-calligraphy.svg",
    },
    {
      id: 5,
      dateBadge: "Épargne Tayba Travel",
      title: "Épargnez pour votre Omra & Hajj",
      description:
        "Préparez sereinement votre Omra ou votre Hajj grâce à des solutions d’épargne conformes aux principes islamiques. Des versements flexibles, accessibles et transparents pour vous accompagner pas à pas jusqu’à l’accomplissement de votre projet spirituel.",
      ctaText: "Voir nos formules",
      ctaLink: "/epargne",
      backgroundImage: "/images/slider-epargne-bkg.webp",
      backgroundImageMobile: "/images/slider-epargne-bkg-mobile.webp",
    },
  ],
});

const currentIndex = ref(0);
const autoplayInterval = ref<ReturnType<typeof setInterval> | null>(null);
const progressPercent = ref(0);
const autoplayDuration = 6000;
const rafId = ref<number | null>(null);
const touchStartX = ref<number | null>(null);

const currentSlide = computed(
  () => props.slides[currentIndex.value] ?? props.slides[0],
);

function nextSlide() {
  currentIndex.value = (currentIndex.value + 1) % props.slides.length;
  resetAutoplay();
}

function prevSlide() {
  currentIndex.value =
    (currentIndex.value - 1 + props.slides.length) % props.slides.length;
  resetAutoplay();
}

function goToSlide(index: number) {
  currentIndex.value = index;
  resetAutoplay();
}

function startAutoplay() {
  const start = performance.now();
  const tick = (now: number) => {
    const elapsed = now - start;
    const progress = Math.min(elapsed / autoplayDuration, 1);
    progressPercent.value = progress * 100;
    if (progress >= 1) {
      currentIndex.value = (currentIndex.value + 1) % props.slides.length;
      resetAutoplay();
      return;
    }
    rafId.value = requestAnimationFrame(tick);
  };
  rafId.value = requestAnimationFrame(tick);
}

function resetAutoplay() {
  if (autoplayInterval.value) clearInterval(autoplayInterval.value);
  if (rafId.value) cancelAnimationFrame(rafId.value);
  progressPercent.value = 0;
  startAutoplay();
}

function onTouchStart(event: TouchEvent) {
  touchStartX.value = event.touches[0]?.clientX ?? null;
}

function onTouchEnd(event: TouchEvent) {
  const startX = touchStartX.value;
  const endX = event.changedTouches[0]?.clientX ?? null;
  touchStartX.value = null;
  if (startX === null || endX === null) return;
  const deltaX = endX - startX;
  if (Math.abs(deltaX) < 40) return;
  if (deltaX < 0) nextSlide();
  else prevSlide();
}

onMounted(() => {
  startAutoplay();
});

onUnmounted(() => {
  if (autoplayInterval.value) {
    clearInterval(autoplayInterval.value);
  }
  if (rafId.value) {
    cancelAnimationFrame(rafId.value);
  }
});
</script>

<style scoped>
/* ============================================
   WOW EFFECT ENTRANCE ANIMATIONS
   ============================================ */

/* Main section entrance - fade only */
@keyframes hero-section-entrance {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.hero-section-enter {
  animation: hero-section-entrance 1.2s cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* Background entrance with fade only */
@keyframes hero-background-entrance {
  0% {
    opacity: 0;
    filter: brightness(0.7) blur(8px);
  }
  100% {
    opacity: 1;
    filter: brightness(1) blur(0px);
  }
}

.hero-background-enter {
  animation: hero-background-entrance 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.1s
    both;
}

/* Calligraphy entrance - fade only */
@keyframes hero-calligraphy-entrance {
  0% {
    opacity: 0;
    filter: blur(6px);
  }
  100% {
    opacity: 1;
    filter: blur(0px);
  }
}

.hero-calligraphy-enter {
  animation: hero-calligraphy-entrance 1.3s ease-in-out 0.4s both;
}

.hero-calligraphy-desktop-enter {
  animation: hero-calligraphy-entrance 1.3s ease-in-out 0.5s both;
}

/* Content card entrance - fade only */
@keyframes hero-content-entrance {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.hero-content-enter {
  animation: hero-content-entrance 1s ease-in-out 0.3s both;
}

.hero-content-desktop-enter {
  animation: hero-content-entrance 1s ease-in-out 0.4s both;
}

/* Controls entrance - fade in from bottom */
@keyframes hero-controls-entrance {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-controls-enter {
  animation: hero-controls-entrance 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.7s both;
}

/* Social links entrance - slide from right with rotation */
@keyframes hero-social-entrance {
  0% {
    opacity: 0;
    transform: translateX(40px) rotate(12deg);
  }
  100% {
    opacity: 1;
    transform: translateX(0) rotate(0deg);
  }
}

.hero-social-enter {
  animation: hero-social-entrance 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s
    both;
}

.hero-social-desktop-enter {
  animation: hero-social-entrance 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s
    both;
}

/* Desktop layout entrance */
@keyframes hero-desktop-entrance {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.hero-desktop-enter {
  animation: hero-desktop-entrance 0.6s ease-out 0.2s both;
}

/* ============================================
   SLIDE TRANSITIONS
   ============================================ */

/* Background slide fade - fade only */
.slide-fade-enter-active {
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-leave-active {
  transition: opacity 0.8s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.slide-fade-enter-from {
  opacity: 0;
}

.slide-fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
}

/* Content slide animation - fade only */
.slide-content-enter-active {
  transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: 0.15s;
}

.slide-content-leave-active {
  transition: opacity 0.5s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.slide-content-enter-from {
  opacity: 0;
}

.slide-content-leave-to {
  opacity: 0;
}

.slide-content-enter-to,
.slide-content-leave-from {
  opacity: 1;
}

/* Calligraphy animation - blur out then fade in */
.calligraphy-fade-enter-active {
  transition:
    opacity 0.5s ease-in-out,
    filter 0.5s ease-in-out;
}

.calligraphy-fade-leave-active {
  transition:
    opacity 0.35s ease-in-out,
    filter 0.35s ease-in-out;
}

.calligraphy-fade-enter-from {
  opacity: 0;
  filter: blur(8px);
}

.calligraphy-fade-leave-to {
  opacity: 0;
  filter: blur(8px);
}

.calligraphy-fade-enter-to,
.calligraphy-fade-leave-from {
  opacity: 1;
  filter: blur(0px);
}

/* Mobile card content fade only */
.card-fade-enter-active {
  transition: opacity 0.5s ease-in-out;
  transition-delay: 0.1s;
}

.card-fade-leave-active {
  transition: opacity 0.35s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.card-fade-enter-from {
  opacity: 0;
}

.card-fade-leave-to {
  opacity: 0;
}

.card-fade-enter-to,
.card-fade-leave-from {
  opacity: 1;
}

.calligraphy-wrap {
  left: calc(50% + 150px);
  width: 605px;
  height: 605px;
}

@media (max-width: 1600px) {
  .calligraphy-wrap {
    left: calc(50% + 90px);
    width: 540px;
    height: 540px;
  }
}
</style>
